<script lang="ts">
export type ProjectTreeNode = FolderInTree<{ nodeType: 'agent' } & Project>
</script>

<script setup lang="ts">
import * as treeView from '@zag-js/tree-view'
import { normalizeProps, useMachine } from '@zag-js/vue'
import { computed, nextTick, ref, useAttrs, useId, watch } from 'vue'
import { AGENT_NAME_FALLBACK, FOLDER_NAME_FALLBACK } from '../Project/constants'
import { folderEventBus } from '../Project/Folders/folderEventBus'
import type { FolderInTree } from '../Project/Folders/types'
import { useFolderStore } from '../Project/Folders/useFolderStore'
import FolderPermissionsDialog from '../Project/Permissions/FolderPermissionsDialog.vue'
import ProjectTreeNode from './ProjectTreeNode.vue'
import type { Project } from './useProjects'
import { useTreeContextProvider } from './useProjectTreeContext'
import { useProjectTreeSearch } from './useProjectTreeSearch'

/**
 * This component renders a tree of projects and folders.
 *
 * Semantically, this is an element with
 * [role="tree"](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tree_role)
 * , and it should behave as such.
 *
 * See https://zagjs.com/components/vue/tree-view for information on how to use
 * the headless Tree View component provided by zag-js.
 */

const props = defineProps<{
  searchTerm?: string
}>()

const folderStore = useFolderStore()

const { projectTree, expandAncestorsOfMatches } = useProjectTreeSearch(
  computed(() => props.searchTerm),
)

const id = useId()
const attrs = useAttrs()
const service = useMachine(
  treeView.machine,
  computed(() => ({
    id,
    collection: treeView.collection<ProjectTreeNode>({
      nodeToValue: (node) => node.id,
      nodeToString: (node) => {
        if (node.nodeType === 'folder') {
          return node.name || FOLDER_NAME_FALLBACK
        }

        return node.name || AGENT_NAME_FALLBACK
      },
      rootNode: {
        id: 'ROOT',
        name: '',
        children: projectTree.value,
        nodeType: 'folder',
      },
    }),
    ids: {
      label: attrs.ariaLabelledby as string | undefined,
    },
  })),
)
const api = computed(() => treeView.connect(service, normalizeProps))

// Auto-expand folders that contain matching items when searching
watch(
  () => props.searchTerm,
  (newSearchTerm) => {
    expandAncestorsOfMatches(api.value, newSearchTerm)
  },
  { immediate: true },
)

useTreeContextProvider()
// Track which folder ID is currently being renamed
const editingNameId = ref<string | null>(null)

// Handle rename request from any node in the tree
const handleRename = (folderId: string) => {
  editingNameId.value = folderId
}

// Clear the editing state (called when editing is completed or canceled)
const clearEditing = () => {
  editingNameId.value = null
}

folderEventBus.on((eventName, payload) => {
  if (eventName === 'create-folder' && payload) {
    editingNameId.value = payload.folderId
  }
})

const dialogFolder = ref<Pick<FolderInTree, 'id' | 'name'> | null>(null)
const dialogIsOpen = ref(false)
const onManagePermissions = async (folderId: string) => {
  dialogFolder.value = folderStore.folderMap[folderId] ?? null

  // Wait for the dialog to be mounted before opening it, so that we transition in
  await nextTick()

  dialogIsOpen.value = true
}

const onCloseDialog = async () => {
  dialogIsOpen.value = false

  // Wait for the dialog to be unmounted before clearing the folder reference, so that we transition out
  await nextTick()

  dialogFolder.value = null
}

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <div v-bind="{ ...api.getRootProps(), ...$attrs }">
    <div v-bind="api.getTreeProps()">
      <ProjectTreeNode
        v-for="(node, index) in api.collection.rootNode.children"
        :key="node.id"
        :node="node"
        :index-path="[index]"
        :api="api"
        :editing-id="editingNameId"
        @rename="handleRename"
        @clear-editing="clearEditing"
        @manage-permissions="onManagePermissions"
      />
    </div>
  </div>
  <FolderPermissionsDialog
    v-if="dialogFolder"
    :open="dialogIsOpen"
    :folder="dialogFolder"
    @close="onCloseDialog"
  />
</template>
